function _typeof(o) { "@babel/helpers - typeof"; return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (o) { return typeof o; } : function (o) { return o && "function" == typeof Symbol && o.constructor === Symbol && o !== Symbol.prototype ? "symbol" : typeof o; }, _typeof(o); }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == _typeof(i) ? i : String(i); }
function _toPrimitive(t, r) { if ("object" != _typeof(t) || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != _typeof(i)) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import "core-js/modules/es.array.includes.js";
import "core-js/modules/es.string.includes.js";
import "core-js/modules/es.array.concat.js";
import "core-js/modules/es.object.assign.js";
import "core-js/modules/es.symbol.to-primitive.js";
import "core-js/modules/es.date.to-primitive.js";
import "core-js/modules/es.symbol.js";
import "core-js/modules/es.symbol.description.js";
import "core-js/modules/es.object.to-string.js";
import "core-js/modules/es.number.constructor.js";
import "core-js/modules/es.object.define-property.js";
import "core-js/modules/es.symbol.iterator.js";
import "core-js/modules/es.array.iterator.js";
import "core-js/modules/es.string.iterator.js";
import "core-js/modules/web.dom-collections.iterator.js";
import { OpenInNewOutlineIcon } from '@applyboard/ui-icons';
import React from 'react';
import { InternalButton } from "../InternalButton";
import { SrOnly } from "../SrOnly";
import { useLinkAs } from "../LinkAsContext";
import { allowedTooltipSymbols } from "../../utils/tooltip";
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { jsxs as _jsxs } from "@emotion/react/jsx-runtime";
/**
 * This is a link that looks like a button.
 *
 * ## Accessibility ♿
 * - This should be used instead of a button whenever any sort of navigation
 *   occurs.  This is important so that the link shows up in the list of links
 *   for screen readers.  It's also important for link functionality, such as
 *   "right click + open in new tab", "CMD+click", "right click + copy link",
 *   etc.
 * - If there is no navigation, do not use this component - use a regular Button
 *   instead.  For more information on when to use button vs link and link
 *   accessibility in general, see [this documentation](https://applyboard.atlassian.net/wiki/spaces/UE/pages/2728788050/Link+Accessibility)
 * - Accessibility features:
 *   - Visible focus ring for keyboard users
 *   - Space activates the link
 *   - Awkward name for `mode="override_user_choice_and_open_in_new_tab"` to encourage consumers to think whether it's a good idea to open a link in a new tab
 *   - When link opens in new tab by default, adds an accompanying external link icon and "(Opens in new tab)" as invisible text for screen readers
 */
export var InternalButtonLink = /*#__PURE__*/React.forwardRef(function InternalButtonLink(_ref, ref) {
  var ariaDescribedby = _ref['aria-describedby'],
    ariaLabel = _ref['aria-label'],
    children = _ref.children,
    _ref$disabled = _ref.disabled,
    disabled = _ref$disabled === void 0 ? false : _ref$disabled,
    _ref$emphasis = _ref.emphasis,
    emphasis = _ref$emphasis === void 0 ? 'contained' : _ref$emphasis,
    href = _ref.href,
    id = _ref.id,
    _ref$intent = _ref.intent,
    intent = _ref$intent === void 0 ? 'secondary' : _ref$intent,
    leadIcon = _ref.leadIcon,
    _ref$loading = _ref.loading,
    loading = _ref$loading === void 0 ? false : _ref$loading,
    m = _ref.m,
    mb = _ref.mb,
    ml = _ref.ml,
    mode = _ref.mode,
    mr = _ref.mr,
    mt = _ref.mt,
    mx = _ref.mx,
    my = _ref.my,
    onBlur = _ref.onBlur,
    _onClick = _ref.onClick,
    onFocus = _ref.onFocus,
    onPointerDown = _ref.onPointerDown,
    onPointerLeave = _ref.onPointerLeave,
    onPointerMove = _ref.onPointerMove,
    _ref$rel = _ref.rel,
    rel = _ref$rel === void 0 ? 'noopener' : _ref$rel,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? 'md' : _ref$size,
    trailIcon = _ref.trailIcon,
    variant = _ref.variant,
    _ref$width = _ref.width,
    width = _ref$width === void 0 ? 'hug' : _ref$width,
    download = _ref.download,
    onAfterHistoryPush = _ref.onAfterHistoryPush;
  var isNewTabLink = mode === 'override_user_choice_and_open_in_new_tab';
  if (isNewTabLink) {
    if (children && trailIcon) {
      throw new Error('`trailIcon` is not supported in ButtonLink when the link opens in a new tab.');
    } else if (!children && leadIcon) {
      throw new Error('`leadIcon` is not supported in icon-only ButtonLink when the link opens in a new tab.');
    }
  }
  var LinkAs = useLinkAs();
  if (!rel.includes('noopener')) {
    throw new Error('[ButtonLink] ButtonLink was rendered without including "noopener" in the "rel" attribute. This is a potential security vulnerability.');
  }
  return _jsxs(InternalButton, {
    "aria-describedby": ariaDescribedby
    // To prevent consumers from using `aria-label` like a poor man's testid,
    // let's not allow passing `aria-label` when there's text for now until there's
    // a use-case
    ,
    "aria-label": children ? undefined : !ariaLabel ? undefined : "".concat(ariaLabel).concat(isNewTabLink ? ' (Opens in new tab)' : ''),
    as: LinkAs,
    disabled: disabled,
    emphasis: emphasis,
    href: href,
    id: id,
    intent: intent,
    leadIcon: isNewTabLink && !children ? OpenInNewOutlineIcon : leadIcon,
    loading: loading,
    m: m,
    mb: mb,
    ml: ml,
    mr: mr,
    mt: mt,
    mx: mx,
    my: my,
    onBlur: onBlur
    // Don't pass onClick directly so we don't expose the event object to consumers at run-time
    ,
    onClick: function onClick() {
      return _onClick === null || _onClick === void 0 ? void 0 : _onClick();
    },
    onFocus: onFocus,
    onPointerDown: onPointerDown,
    onPointerLeave: onPointerLeave,
    onPointerMove: onPointerMove,
    ref: ref,
    rel: rel,
    size: size,
    target: isNewTabLink ? '_blank' : undefined,
    trailIcon: isNewTabLink && !!children ? OpenInNewOutlineIcon : isNewTabLink && !children ? undefined : trailIcon,
    variant: variant,
    width: width,
    download: download,
    onAfterHistoryPush: onAfterHistoryPush,
    children: [children, isNewTabLink && children && _jsx(SrOnly, {
      children: "\xA0(Opens in new tab)"
    })]
  });
});
Object.assign(InternalButtonLink, _defineProperty({}, allowedTooltipSymbols.ButtonLink, true));